'use client'

import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { CheckCircle, ArrowRight, ChevronDown } from 'lucide-react'
import FAQ from '@/components/landing/FAQ'
import ClientBottomCTA from '@/components/landing/ClientBottomCTA'
import ClientExploreCategories from '@/components/landing/ClientExploreCategories'
import ClientBigPromoCard from '@/components/landing/ClientBigPromoCard'
import ClientPricingCards from '@/components/landing/ClientPricingCards'
import ClientTrustAndTestimonials from '@/components/landing/ClientTrustAndTestimonials'
import { trackLandingEvent } from '@/lib/landing-analytics'

export default function ClientLandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0) // 첫 항목 기본 오픈
  const [isPricingOpen, setIsPricingOpen] = useState(false)
  const [heroCategory, setHeroCategory] = useState('')
  const [heroBrief, setHeroBrief] = useState('')
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set())
  const sectionRefs = useRef<(HTMLElement | null)[]>([])

  const buildLoginRedirect = (target: string) => `/auth?redirect=${encodeURIComponent(target)}`
  const buildRegistrationHref = (source: string) => {
    const params = new URLSearchParams({ from: source })
    return buildLoginRedirect(`/client/projects/new?${params.toString()}`)
  }
  const heroQuickStartCategories = [
    { label: '웹·앱 개발', mainCategory: '웹 개발' },
    { label: 'DevOps·인프라', mainCategory: '인프라/클라우드/보안' },
    { label: 'AI·데이터 분석', mainCategory: '인공지능(AI)/데이터' },
    { label: 'UI/UX 디자인', mainCategory: '디자인/미디어' },
    { label: '서비스 기획(PM)', mainCategory: '기획/PM' },
  ]

  const buildQuickStartHref = () => {
    const params = new URLSearchParams()
    const trimmedBrief = heroBrief.trim()

    if (heroCategory) params.set('mainCategory', heroCategory)
    if (trimmedBrief) params.set('brief', trimmedBrief)
    params.set('forcePrefill', '1')
    params.set('from', 'landing_quick_start')

    const query = params.toString()
    return buildLoginRedirect(`/client/projects/new${query ? `?${query}` : ''}`)
  }

  const handleQuickStartSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    trackLandingEvent('hero_quick_start_submit', {
      has_category: Boolean(heroCategory),
      has_brief: Boolean(heroBrief.trim()),
    })
    window.location.href = buildQuickStartHref()
  }

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const handleFaqKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleFaq(index)
    }
  }

  // Intersection Observer로 스크롤 애니메이션 처리
  useEffect(() => {
    const observers: IntersectionObserver[] = []

    sectionRefs.current.forEach((section, index) => {
      if (!section) return

      // 초기 렌더링 시 첫 번째 섹션들은 바로 보이도록 설정
      const rect = section.getBoundingClientRect()
      const isInitiallyVisible = rect.top < window.innerHeight * 0.8
      if (isInitiallyVisible) {
        setVisibleSections((prev) => new Set(prev).add(index))
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => new Set(prev).add(index))
            }
          })
        },
        {
          threshold: 0.05,
          rootMargin: '0px 0px -100px 0px',
        }
      )

      observer.observe(section)
      observers.push(observer)
    })

    return () => {
      observers.forEach((observer) => observer.disconnect())
    }
  }, [])

  const IMG = {
    projectBrief: '/images/howitworks/project-brief.jpg',
    compareProposals: '/images/howitworks/compare-proposals.jpg',
    handshake: '/images/howitworks/handshake.jpg',
  } as const

  const clientSteps = [
    {
      step: 1,
      title: '프로젝트 등록',
      outcome: '필수 정보만 3분 안에 정리됩니다',
      bullets: ['예산/기간/인력 입력', '필요 기술/역할 선택'],
      meta: '3분',
      imageSrc: IMG.projectBrief,
      imageAlt: 'Project brief illustration',
    },
    {
      step: 2,
      title: '견적 수신 & 비교',
      outcome: '같은 조건으로 견적을 비교할 수 있습니다',
      bullets: ['가격/기간/구성 비교', '제안서 한 화면 확인'],
      meta: '최대 N개 팀(베타 기준)',
      imageSrc: IMG.compareProposals,
      imageAlt: 'Compare proposals illustration',
    },
    {
      step: 3,
      title: '팀 선택 & 진행',
      outcome: '원하면 미팅·계약 단계까지 이어집니다',
      bullets: ['채팅으로 조건 조율', '일정/범위 확정'],
      meta: '비교 카드 + 채팅',
      imageSrc: IMG.handshake,
      imageAlt: 'Handshake collaboration illustration',
    },
  ]

  const faqs = [
    {
      question: '검증은 어떻게 진행되나요?',
      summary: '포트폴리오·경력·사업자·평판 항목을 체크합니다',
      answer:
        '팀 참여 전 기본 신원/사업자 정보, 포트폴리오, 수행 경력, 평판 데이터를 기준으로 검증합니다. 검증 항목은 서비스 내에서 확인할 수 있도록 계속 고도화하고 있습니다.',
    },
    {
      question: '견적은 보통 얼마나 빨리 도착하나요?',
      summary: '평균 첫 견적 도착 시간 6시간(최근 30일 기준)',
      answer:
        '요청 등록 후 평균 6시간 내 첫 견적이 도착합니다(최근 30일 기준). 카테고리와 요청 상세도에 따라 실제 시간은 달라질 수 있습니다.',
    },
    {
      question: '요청만 하고 팀을 선택하지 않아도 되나요?',
      summary: '네, 요청 후 비교만 하고 보류해도 됩니다',
      answer:
        '가능합니다. 견적을 받은 뒤 비교만 하고 선택하지 않아도 되며, 원할 때만 미팅/계약으로 이어가면 됩니다.',
    },
    {
      question: '무료로 어디까지 가능한가요?',
      summary: '프로젝트 등록 + 요청 10회 무료, 추가는 크레딧으로 확장',
      answer:
        '프로젝트 등록과 견적 요청 10회까지 무료입니다. 10회 이후 추가 요청이 필요하면 크레딧으로 확장할 수 있습니다.',
    },
    {
      question: '요청 크레딧은 무엇인가요?',
      summary: '1요청=1크레딧, 필요할 때만 충전',
      answer:
        '무료 제공(10회)을 초과해 추가로 견적 요청을 보낼 때 사용하는 포인트입니다. 1요청 = 1크레딧이며, 필요할 때만 충전해 사용할 수 있습니다. 월 구독 없이 건별로 유연하게 이용할 수 있습니다.',
    },
    {
      question: '프로젝트 등록은 어떻게 하나요?',
      summary: '요구사항/예산/기간/역할 입력 후 요청서 자동 정리',
      answer:
        '프로젝트 정보를 입력하면 요청서가 정리되고, 팀에게 요청을 보낼 수 있습니다. 입력 항목: 요구사항, 예산, 기간, 필요 역할(또는 기술 스택)입니다. 등록된 프로젝트는 검증된 팀과 프리랜서에게 공개되어 견적을 받을 수 있습니다.',
    },
    {
      question: '견적 비교는 무엇이 다른가요?',
      summary: '기간·범위·인력·비용 항목을 통일해 비교합니다',
      answer:
        '여러 팀의 제안을 한 화면에서 비교할 수 있습니다. 비교 항목은 가격, 기간, 인력 구성, 제안 범위(산출물), 커뮤니케이션 방식입니다. 동일한 기준으로 확인하고 필요한 조건은 채팅으로 바로 조율할 수 있습니다.',
    },
  ]

  return (
    <div className="w-full min-h-screen bg-white">
      {/* ✅ Header는 최상단 app/layout.tsx에서 한 번만 렌더링됨 */}
      {/* ✅ fixed header(64px) 아래로 콘텐츠 밀기 */}
      <main className="w-full min-h-[calc(100vh-64px)] pt-[64px] md:pt-[65px]">
        {/* ===============================
         * Hero Section - Dark Theme
         * =============================== */}
        <section className="relative w-full overflow-hidden bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 px-4 py-20 sm:px-6 sm:py-24 md:py-32 lg:px-8">
          {/* 우측 블루 그라데이션 */}
          <div className="absolute right-0 top-0 h-full w-1/2 bg-gradient-to-l from-blue-600/20 via-blue-500/10 to-transparent pointer-events-none" />
          
          <div className="relative mx-auto max-w-7xl">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              {/* 좌측: 텍스트 블록 */}
              <div className="max-w-2xl lg:max-w-none lg:ml-8 xl:ml-12">
                <h1 className="mb-6 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl md:text-6xl">
                  검증된 팀 견적을{' '}
                  <br className="lg:hidden" />
                  한 번에 받고
                  <br />
                  <span className="text-blue-400">비교해서 결정하세요</span>
                </h1>
                <p className="mb-10 text-lg leading-relaxed text-gray-300 sm:text-xl">
                  3분 등록 · 견적 요청 10회 무료 · 평균 6시간 내 첫 응답
                </p>
                <p className="-mt-6 mb-8 text-sm leading-relaxed text-gray-200 sm:text-base">
                  검증 기준(포트폴리오/경력/평판/계약 가능 여부) 확인 후 매칭하며,
                  최근 30일 누적 1,200+ 요청이 접수되었습니다.
                </p>
                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                  <Button
                    asChild
                    size="lg"
                    className="h-12 shrink-0 rounded-xl bg-blue-600 px-8 sm:px-10 text-white shadow-md transition-all hover:bg-blue-700 hover:shadow-lg font-semibold text-sm sm:text-base whitespace-nowrap"
                  >
                    <Link
                      href={buildRegistrationHref('landing_hero_primary')}
                      onClick={() => trackLandingEvent('click_cta_hero_primary')}
                    >
                      무료로 견적 요청 시작하기
                    </Link>
                  </Button>
                  <Link
                    href="#trust-evidence"
                    onClick={() => trackLandingEvent('click_secondary_explore', { source: 'hero' })}
                    className="text-sm font-medium text-gray-300 transition-colors hover:text-white hover:underline"
                  >
                    검증 기준 보기
                  </Link>
                </div>
                <p className="mt-4 text-sm text-gray-300">
                  첫 화면에서는 카테고리와 프로젝트명만 입력하고, 상세 정보는 다음 단계에서 작성합니다.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white">
                    3분 등록
                  </span>
                  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white">
                    요청 10회 무료
                  </span>
                  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white">
                    검증된 팀 추천
                  </span>
                </div>
                <form onSubmit={handleQuickStartSubmit} className="mt-6 w-full max-w-xl rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur-sm">
                  <p className="mb-2 text-xs font-medium text-gray-200">바로 시작하기 · 1단계 (필수 2개)</p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <select
                      value={heroCategory}
                      onChange={(e) => {
                        setHeroCategory(e.target.value)
                        if (e.target.value) {
                          trackLandingEvent('hero_quick_start_category_selected', { main_category: e.target.value })
                        }
                      }}
                      className="h-11 rounded-xl border border-white/20 bg-white/15 px-3 text-sm text-white outline-none transition focus:border-blue-300"
                    >
                      <option value="" className="text-gray-900">카테고리 선택</option>
                      {heroQuickStartCategories.map((category) => (
                        <option key={category.mainCategory} value={category.mainCategory} className="text-gray-900">
                          {category.label}
                        </option>
                      ))}
                    </select>
                    <input
                      value={heroBrief}
                      onChange={(e) => setHeroBrief(e.target.value)}
                      placeholder="예: 쇼핑몰 리뉴얼(PC/Mobile), 결제/로그인 포함"
                      className="h-11 flex-1 rounded-xl border border-white/20 bg-white/15 px-3 text-sm text-white placeholder:text-gray-300 outline-none transition focus:border-blue-300"
                    />
                    <Button
                      type="submit"
                      className="h-11 shrink-0 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                    >
                      다음 단계로
                    </Button>
                  </div>
                </form>
                <p className="mt-3 text-xs text-gray-300 lg:hidden">
                  등록 후 최대 N개 팀 제안이 도착하고 비교 카드와 채팅으로 바로 조율할 수 있습니다. (베타 기준)
                </p>
              </div>

              {/* 우측: 프로세스 미니 카드 */}
              <div className="hidden lg:flex flex-col gap-4 items-end">
                <div className="w-full max-w-sm space-y-4">
                  {/* 카드 1: 요청 전송 */}
                  <div className="relative rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-5 shadow-xl hover:bg-white/15 transition-all">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <ArrowRight className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-blue-400">Step 1</span>
                          <h3 className="text-base font-semibold text-white">등록 후 즉시 요청 발송</h3>
                        </div>
                        <p className="text-sm text-gray-200 leading-snug">
                          요구사항 입력을 완료하면 검증된 팀에게 요청이 전송되고 견적이 도착합니다.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 카드 2: 견적 비교 */}
                  <div className="relative rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-5 shadow-xl hover:bg-white/15 transition-all">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-green-400">Step 2</span>
                          <h3 className="text-base font-semibold text-white">도착한 견적 비교</h3>
                        </div>
                        <p className="text-sm text-gray-200 leading-snug">
                          도착한 제안을 비교 화면에서 확인하고 채팅으로 조건을 조율할 수 있습니다.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="relative rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-5 shadow-xl hover:bg-white/15 transition-all">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-indigo-300" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-indigo-300">Step 3</span>
                          <h3 className="text-base font-semibold text-white">원하면 미팅·계약 진행</h3>
                        </div>
                        <p className="text-sm text-gray-200 leading-snug">
                          팀 선택 후 미팅과 계약 단계까지 연계해 협업을 시작할 수 있습니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===============================
         * Explore Categories
         * =============================== */}
        <div
          ref={(el) => {
            sectionRefs.current[0] = el
          }}
        >
          <ClientExploreCategories isVisible={visibleSections.has(0)} buildLoginRedirect={buildLoginRedirect} />
        </div>

        {/* ===============================
         * How It Works Section
         * =============================== */}
        <section
          id="how-it-works"
          ref={(el) => {
            sectionRefs.current[1] = el
          }}
          className={`w-full bg-gradient-to-b from-white to-slate-50 px-4 py-16 sm:px-6 md:py-20 lg:px-8 transition-all duration-700 ${
            visibleSections.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="mx-auto max-w-6xl">
            <div className="mb-10 text-center">
              <h2 className="mb-3 text-3xl font-bold text-slate-900 md:text-4xl lg:text-5xl">이렇게 진행됩니다</h2>
            </div>

            {/* 카드 레일 */}
            <div className="relative">
              <div className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-slate-50 to-transparent md:hidden" />

              <div
                className={[
                  'flex gap-4 overflow-x-auto pb-2 md:overflow-visible',
                  'snap-x snap-mandatory md:snap-none',
                  '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
                  'md:grid md:grid-cols-3 md:gap-6',
                ].join(' ')}
              >
                {clientSteps.map((step, index) => (
                  <div
                    key={step.step}
                    className={[
                      'snap-start shrink-0 w-[260px] sm:w-[300px] md:w-auto',
                      'group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md flex flex-col',
                      visibleSections.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
                    ].join(' ')}
                    style={{ transitionDelay: visibleSections.has(1) ? `${index * 90}ms` : '0ms' }}
                  >
                    {/* Step 배지 - 상단 좌측 */}
                    <div className="mb-3 flex items-center gap-2">
                      <div className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                        {String(step.step).padStart(2, '0')}
                      </div>
                    </div>

                    {/* 썸네일 - 높이 줄임 */}
                    <div className="relative mb-4 h-[90px] w-full overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-100">
                      {step.imageSrc ? (
                        <Image
                          src={step.imageSrc}
                          alt={step.imageAlt ?? ''}
                          fill
                          className="object-cover"
                          sizes="(max-width: 640px) 260px, 300px"
                          priority={index === 0}
                        />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-blue-50 via-white to-slate-50" />
                      )}
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white/25 to-transparent" />
                    </div>

                    {/* 제목 */}
                    <h3 className="mb-3 text-lg font-semibold leading-tight text-slate-900">{step.title}</h3>

                    {/* Outcome - 가장 크고 진하게 */}
                    <div className="mb-4 flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-slate-700 flex-shrink-0 mt-0.5" />
                      <p className="text-base font-bold leading-snug text-slate-900">{step.outcome}</p>
                    </div>

                    {/* Bullets */}
                    <div className="mt-auto space-y-1.5">
                      {step.bullets.map((bullet, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                          <span className="text-slate-400">·</span>
                          <span>{bullet}</span>
                        </div>
                      ))}
                      <div className="pt-2 text-xs font-semibold text-blue-700">
                        {step.meta}
                      </div>
                    </div>

                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-blue-500/0 via-blue-500/40 to-blue-500/0 opacity-0 transition group-hover:opacity-100" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===============================
         * Big Promo Card
         * =============================== */}
        <section
          ref={(el) => {
            sectionRefs.current[2] = el
          }}
        >
          <ClientBigPromoCard buildLoginRedirect={buildLoginRedirect} isVisible={visibleSections.has(2)} />
        </section>

        {/* ===============================
         * Trust and Testimonials
         * =============================== */}
        <section
          id="trust-evidence"
          ref={(el) => {
            sectionRefs.current[4] = el
          }}
        >
          <ClientTrustAndTestimonials isVisible={visibleSections.has(4)} />
        </section>

        {/* ===============================
         * FAQ Section
         * =============================== */}
        <div
          ref={(el) => {
            sectionRefs.current[5] = el
          }}
        >
          <FAQ
            faqs={faqs}
            openFaq={openFaq}
            toggleFaq={toggleFaq}
            handleFaqKeyDown={handleFaqKeyDown}
            isVisible={visibleSections.has(5)}
          />
        </div>

        {/* ===============================
         * Pricing Cards (접기)
         * =============================== */}
        <section
          id="pricing"
          ref={(el) => {
            sectionRefs.current[3] = el
          }}
          className="w-full bg-white px-4 pb-4 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-6xl">
            <button
              type="button"
              onClick={() => setIsPricingOpen((prev) => !prev)}
              className="mx-auto flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-left transition-colors hover:bg-gray-100"
            >
              <div>
                <p className="text-sm font-semibold text-gray-900">프로젝트 등록 3분, 요청 10회 무료</p>
                <p className="text-xs text-gray-500">가격/크레딧 정보 보기</p>
              </div>
              <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform ${isPricingOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
          {isPricingOpen && (
            <ClientPricingCards buildLoginRedirect={buildLoginRedirect} isVisible={true} />
          )}
        </section>

        {/* ===============================
         * Bottom CTA
         * =============================== */}
        <ClientBottomCTA buildLoginRedirect={buildLoginRedirect} />
      </main>
    </div>
  )
}
